/**
 * Conditions de déblocage des cosmétiques (cadres, bannières, thèmes HUD).
 *
 * ⚠ Écrit en `.mjs` **volontairement** : client et serveur importent ce fichier-ci, il n'y a
 * pas de copie à synchroniser. Les types vivent dans `cosmetic_unlocks.d.mts`.
 *
 * Pourquoi les conditions sont ici et non dans `client/app/data/cosmetics.ts` : la liste
 * `stats._lifetimeUnlockedCosmetics` — la mémoire qui fait survivre un cosmétique au reset
 * d'ascension — était alimentée par le client sans aucune vérification. N'importe quel
 * identifiant pouvait y être injecté. Pour que le serveur puisse trancher, il doit connaître
 * les conditions ; pour qu'elles ne divergent jamais entre les deux côtés, il ne doit en
 * exister qu'une seule définition. C'est celle-ci : `cosmetics.ts` s'y réfère par identifiant.
 */

/** Exigence par cosmétique. Voir `evaluateCosmeticRequirement` pour la sémantique. */
export const COSMETIC_UNLOCK_REQUIREMENTS = {
  frame_basic: { t: 'always' },
  frame_kanto: { t: 'badges', min: 13 },
  frame_shiny: { t: 'achievement', id: 'first_shiny' },
  frame_arena: { t: 'badges', min: 85 },
  frame_master: { t: 'achievement', id: 'badges_117' },
  frame_social_blurple: { t: 'level', min: 35 },
  frame_nitro_aura: { t: 'any', of: [{ t: 'level', min: 120 }, { t: 'badges', min: 50 }] },
  frame_boost_fuchsia: { t: 'any', of: [{ t: 'badges', min: 75 }, { t: 'level', min: 100 }] },
  frame_asc_eligible: { t: 'stat', key: '_ascEligibleAt', min: 1 },
  frame_asc_ember: { t: 'stat', key: '_ascLevel', min: 1 },
  frame_asc_celestial: { t: 'stat', key: '_ascLevel', min: 5 },
  frame_ember_core: { t: 'level', min: 55 },
  frame_tidal_crown: { t: 'stat', key: 'totalKills', min: 25000 },
  frame_shadow_orbit: { t: 'stat', key: 'totalDamage', min: 150000000 },
  frame_glacier_sigil: { t: 'pokedexGens', min: 5 },
  frame_stellar_halo: { t: 'badges', min: 105 },
  frame_royal_onyx: { t: 'badges', min: 90 },
  frame_asc_dawn: { t: 'stat', key: '_ascLevel', min: 3 },
  frame_void_paragon: { t: 'stat', key: '_ascLevel', min: 10 },
  frame_asc_season_bronze: { t: 'stat', key: '_ascSeasonReward_frame_bronze', min: 1 },
  frame_asc_overkill: { t: 'ascReward', rewardId: 'asc_synergy_overkill_glass' },
  frame_asc_season_violet: { t: 'stat', key: '_ascSeasonReward_frame_violet', min: 1 },
  frame_asc_season_apex: { t: 'stat', key: '_ascSeasonReward_frame_apex', min: 1 },
  frame_mythic_triad: { t: 'stat', key: '_egMythicCoopClears', min: 3 },
  frame_admin_dragonforged: { t: 'stat', key: '_adminRewardFrame1', min: 1 },
  frame_admin_aurora_crown: { t: 'stat', key: '_adminRewardFrame2', min: 1 },
  frame_admin_voidcrest: { t: 'stat', key: '_adminRewardFrame3', min: 1 },
  frame_patron_glow: { t: 'supporter', tier: 2 },
  frame_patron_aura: { t: 'supporter', tier: 3 },
  frame_community_spark: { t: 'community', id: 'frame_community_spark' },
  banner_default: { t: 'always' },
  banner_pokedex: { t: 'pokedexGens', min: 9 },
  banner_arena: { t: 'badges', min: 90 },
  banner_olympus: { t: 'any', of: [{ t: 'achievement', id: 'secret_shiny_leg_10' }, { t: 'stat', key: 'totalDamage', min: 1000000000 }] },
  banner_asc_nebula: { t: 'stat', key: '_ascLevel', min: 2 },
  banner_asc_eternal: { t: 'stat', key: '_ascLevel', min: 8 },
  banner_sunset_plaza: { t: 'level', min: 45 },
  banner_midnight_lake: { t: 'stat', key: 'totalKills', min: 30000 },
  banner_crystal_spire: { t: 'badges', min: 70 },
  banner_stormfront: { t: 'stat', key: 'totalDamage', min: 200000000 },
  banner_golden_summit: { t: 'badges', min: 80 },
  banner_obsidian_throne: { t: 'badges', min: 100 },
  banner_asc_aurora: { t: 'stat', key: '_ascLevel', min: 4 },
  banner_asc_paragon: { t: 'stat', key: '_ascLevel', min: 12 },
  banner_asc_season_starter: { t: 'stat', key: '_ascSeasonReward_banner_starter', min: 1 },
  banner_asc_season_celestial: { t: 'stat', key: '_ascSeasonReward_banner_celestial', min: 1 },
  banner_mythic_omega: { t: 'stat', key: '_egMythicTotalClears', min: 5 },
  banner_admin_radiant_sky: { t: 'stat', key: '_adminRewardBanner1', min: 1 },
  banner_admin_crimson_gate: { t: 'stat', key: '_adminRewardBanner2', min: 1 },
  banner_admin_astral_archive: { t: 'stat', key: '_adminRewardBanner3', min: 1 },
  banner_patron_sky: { t: 'supporter', tier: 2 },
  banner_patron_golden: { t: 'supporter', tier: 3 },
  banner_community_unity: { t: 'community', id: 'banner_community_unity' },
  hud_classic: { t: 'always' },
  hud_neon: { t: 'level', min: 20 },
  hud_gold: { t: 'badges', min: 24 },
  hud_void: { t: 'stat', key: 'totalDamage', min: 350000000 },
  hud_kanto_retro: { t: 'level', min: 35 },
  hud_johto_pocket: { t: 'badges', min: 40 },
  hud_hoenn_advance: { t: 'badges', min: 50 },
  hud_champion_pixels: { t: 'pokedexGens', min: 6 },
  hud_pixel_town: { t: 'stat', key: 'totalKills', min: 9000 },
  hud_pixel_route: { t: 'stat', key: 'totalKills', min: 20000 },
  hud_pixel_cave: { t: 'stat', key: 'totalDamage', min: 120000000 },
  hud_pixel_league: { t: 'badges', min: 95 },
  hud_biome_forest: { t: 'level', min: 48 },
  hud_biome_ocean: { t: 'stat', key: 'totalDamage', min: 220000000 },
  hud_biome_volcano: { t: 'badges', min: 65 },
  hud_biome_snow: { t: 'pokedexGens', min: 9 },
  hud_asc_crimson: { t: 'stat', key: '_ascLevel', min: 1 },
  hud_asc_astral: { t: 'stat', key: '_ascLevel', min: 4 },
  hud_asc_paragon: { t: 'stat', key: '_ascLevel', min: 7 },
  hud_asc_season_nova: { t: 'stat', key: '_ascSeasonReward_hud_nova', min: 1 },
  hud_mythic_command: { t: 'stat', key: '_egMythicSoloClears', min: 3 },
  hud_admin_celestial_command: { t: 'stat', key: '_adminRewardHud1', min: 1 },
  hud_admin_crimson_royal: { t: 'stat', key: '_adminRewardHud2', min: 1 },
  hud_admin_void_protocol: { t: 'stat', key: '_adminRewardHud3', min: 1 },
  hud_patron_celestial: { t: 'supporter', tier: 2 },
  hud_patron_shimmer: { t: 'supporter', tier: 3 },
  hud_community_glow: { t: 'community', id: 'hud_community_glow' },
  hud_community_radiance: { t: 'community', id: 'hud_community_radiance' },
}

/** Cosmétiques que le serveur ne peut pas trancher seul (état global, hors profil joueur). */
const SERVER_UNVERIFIABLE_KINDS = new Set(['community'])

function asInt(value) {
  const n = Math.floor(Number(value))
  return Number.isFinite(n) ? n : 0
}

/**
 * Compteur de stats. `totalKills` vit sur la ligne joueur autant que dans `stats` :
 * on retient le maximum des deux, comme le faisait `cosmetics.ts`.
 */
function statValue(ctx, key) {
  const fromStats = asInt(ctx?.stats?.[key])
  if (key === 'totalKills') return Math.max(asInt(ctx?.totalKills), fromStats)
  return fromStats
}

function ascRewardsClaimed(ctx) {
  const raw = ctx?.stats?._ascRewardsClaimed
  return Array.isArray(raw) ? raw : []
}

/** Évalue une exigence contre un contexte joueur. Toute forme inconnue est refusée. */
export function evaluateCosmeticRequirement(req, ctx) {
  if (!req || typeof req !== 'object') return false
  switch (req.t) {
    case 'always':
      return true
    case 'level':
      return asInt(ctx?.level) >= req.min
    case 'badges':
      return asInt(ctx?.badges) >= req.min
    case 'pokedexGens':
      return (Array.isArray(ctx?.completedPokedexGens) ? ctx.completedPokedexGens.length : 0) >= req.min
    case 'stat':
      return statValue(ctx, req.key) >= req.min
    case 'achievement':
      return Array.isArray(ctx?.achievements) && ctx.achievements.includes(req.id)
    case 'supporter':
      return asInt(ctx?.supporterTier) >= req.tier
    case 'community':
      return (
        Array.isArray(ctx?.communityUnlockedCosmeticIds) &&
        ctx.communityUnlockedCosmeticIds.includes(req.id)
      )
    case 'ascReward':
      return ascRewardsClaimed(ctx).includes(req.rewardId)
    case 'any':
      return Array.isArray(req.of) && req.of.some((sub) => evaluateCosmeticRequirement(sub, ctx))
    default:
      return false
  }
}

export function getCosmeticUnlockRequirement(cosmeticId) {
  return COSMETIC_UNLOCK_REQUIREMENTS[cosmeticId] ?? null
}

/** Un identifiant hors catalogue n'est jamais débloqué : la liste est fermée. */
export function isCosmeticUnlocked(cosmeticId, ctx) {
  const req = getCosmeticUnlockRequirement(cosmeticId)
  if (!req) return false
  return evaluateCosmeticRequirement(req, ctx)
}

/** Vrai si l'exigence dépend d'un état que le serveur n'évalue pas dans la boucle de save. */
export function isRequirementServerVerifiable(req) {
  if (!req || typeof req !== 'object') return false
  if (req.t === 'any') {
    return Array.isArray(req.of) && req.of.every((sub) => isRequirementServerVerifiable(sub))
  }
  return !SERVER_UNVERIFIABLE_KINDS.has(req.t)
}

/**
 * Cosmétiques que le contexte joueur justifie, pour alimenter
 * `stats._lifetimeUnlockedCosmetics`. `serverSide` écarte les exigences qui dépendent de
 * l'état global (paliers communautaires) : elles restent vraies pour tout le monde tant que
 * le palier tient, et n'ont donc pas besoin de cette mémoire.
 */
export function collectUnlockedCosmeticIds(ctx, { serverSide = false } = {}) {
  const out = []
  for (const [cosmeticId, req] of Object.entries(COSMETIC_UNLOCK_REQUIREMENTS)) {
    if (serverSide && !isRequirementServerVerifiable(req)) continue
    if (evaluateCosmeticRequirement(req, ctx)) out.push(cosmeticId)
  }
  return out
}
